import { LatestWebchatConfig } from '@bpinternal/shared'
import clsx from 'clsx'
import { ArrowUp, Loader } from 'lucide-react'
import { ComponentProps, forwardRef, memo, Ref, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { getUseMessageHistoryStore } from '../../stores'
import { getComposerFileStore } from '../../stores/composerFileStore'
import { webchatClasses } from '../../styles/classes'
import { useTranslations } from '../../contexts/TranslationContext'
import type {
  BlocIntegrationMessageItems,
  ComposableIntegrationMessagePayload,
  IntegrationMessage,
  ScopedClient,
} from '../../types'
import { FileAttachment, FileInputButton, SpeechRecognition, TextInput } from './parts'

type Props = ComponentProps<'div'> & {
  inputRef?: Ref<HTMLTextAreaElement>
  disableComposer?: boolean
  isReadOnly?: boolean
  allowFileUpload?: boolean
  disableSendButton?: boolean
  connected?: boolean
  sendMessage?: ScopedClient['sendMessage']
  uploadFile?: ScopedClient['uploadFile']
  storageKey?: string
  conversationId?: string
} & Pick<LatestWebchatConfig, 'composerPlaceholder' | 'footer'>

export const Composer = memo(
  forwardRef<HTMLDivElement, Props>(
    (
      {
        className,
        inputRef,
        disableComposer,
        isReadOnly,
        allowFileUpload,
        disableSendButton,
        connected,
        composerPlaceholder,
        footer,
        sendMessage,
        uploadFile,
        storageKey,
        conversationId,
        children,
        ...props
      },
      ref
    ) => {
      const { composer } = webchatClasses
      const t = useTranslations()
      const composerDisabled: boolean = disableComposer || isReadOnly || !connected

      const [textInput, setTextInput] = useState('')
      const [historyIndex, setHistoryIndex] = useState(-1)

      const useComposerFileStore = getComposerFileStore(conversationId || 'default')
      const useMessageHistoryStore = getUseMessageHistoryStore(conversationId || 'default', storageKey)

      const composerFiles = useComposerFileStore((state) => state.files)
      const setComposerFiles = useComposerFileStore((state) => state.setFiles)
      const deleteComposerFile = useComposerFileStore((state) => state.deleteFile)
      const uploadComposerFile = useComposerFileStore((state) => state.uploadFile)
      const addMessageToHistory = useMessageHistoryStore((state) => state.addMessageToHistory)

      const uploadedFiles = composerFiles.filter((file) => file.status === 'uploaded')
      const showSendButton = !!textInput.trim() || uploadedFiles.length > 0

      const sendComposerMessage = () => {
        if (disableSendButton || !showSendButton) return

        const messagePayloads: ComposableIntegrationMessagePayload[] =
          uploadedFiles.map((file) => {
            const url = file.url!
            const type = file.type
            const fileId = file.fileId

            const typeToMessage: Record<string, ComposableIntegrationMessagePayload> = {
              video: { type: 'video', videoUrl: url, fileId },
              audio: { type: 'audio', audioUrl: url, fileId },
              image: { type: 'image', imageUrl: url, fileId },
            }

            return typeToMessage[type] ?? { title: file.name, type: 'file', fileUrl: url, fileId }
          }) ?? []

        if (textInput) messagePayloads.push({ type: 'text', text: textInput })

        let messagePayload: IntegrationMessage['payload']
        if (messagePayloads.length == 1) {
          messagePayload = messagePayloads[0]!
        } else {
          const blocItems: BlocIntegrationMessageItems = messagePayloads.map((message) => {
            const { type, ...payload } = message

            return {
              type,
              payload,
            } as BlocIntegrationMessageItems[number]
          })

          messagePayload = {
            type: 'bloc',
            items: blocItems,
          }
        }

        setTextInput('')
        sendMessage?.(messagePayload)
        setComposerFiles([])
        addMessageToHistory(textInput)
        setHistoryIndex(-1)
      }

      if (isReadOnly) return null

      return (
        <>
          <div
            data-disabled={composerDisabled}
            data-waiting={disableSendButton}
            className={clsx(composer.container.className, className)}
            ref={ref}
            {...props}
          >
            {children}
            {composerFiles.length > 0 && (
              <div {...composer?.fileContainer}>
                {composerFiles.map((file) => (
                  <FileAttachment key={file.name} {...file} removeFile={deleteComposerFile} />
                ))}
              </div>
            )}
            <div
              {...composer.inputContainer}
              onClick={() => {
                if (inputRef && 'current' in inputRef && inputRef.current) {
                  inputRef.current.focus()
                }
              }}
            >
              {allowFileUpload && (
                <FileInputButton
                  disabled={composerDisabled}
                  onFileSelected={(file) => {
                    if (uploadFile) uploadComposerFile(file, uploadFile)
                  }}
                />
              )}
              <TextInput
                inputRef={inputRef}
                composerPlaceholder={composerPlaceholder}
                composerDisabled={composerDisabled}
                textInput={textInput}
                historyIndex={historyIndex}
                setTextInput={setTextInput}
                setHistoryIndex={setHistoryIndex}
                sendComposerMessage={sendComposerMessage}
                allowFileUpload={allowFileUpload}
                onFilePasted={(file) => {
                  if (uploadFile) uploadComposerFile(file, uploadFile)
                }}
                conversationId={conversationId}
                storageKey={storageKey}
              />
              {disableSendButton && <Loader {...composer.inputLoader} role="status" data-show={disableSendButton} />}
              {!composerDisabled && !disableSendButton && (
                <>
                  <ArrowUp
                    {...composer.sendbutton}
                    role="button"
                    tabIndex={0}
                    aria-label={t['composer.sendButton.ariaLabel']}
                    data-show={showSendButton}
                    onClick={() => sendComposerMessage()}
                  />
                  <SpeechRecognition disabled={showSendButton} setTextInput={setTextInput} />
                </>
              )}
            </div>
          </div>
          <ReactMarkdown
            components={{
              a: ({ node, ...props }) => <a {...props} target="_blank" rel="noreferrer noopener" />,
            }}
            allowedElements={['a', 'strong', 'code', 'em', 'p', 'span']}
            {...composer.footer}
          >
            {footer}
          </ReactMarkdown>
        </>
      )
    }
  )
)
